import { PostHogProvider, usePostHog } from "@posthog/react";
import type { PropsWithChildren } from "react";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useAuth } from "@/hooks/auth/use-auth";
import { posthogClient } from "./posthog-client";

/**
 * Provider component that handles PostHog initialization and tracking
 */
export function PostHogAnalytics({ children }: PropsWithChildren) {
  return (
    <PostHogProvider client={posthogClient}>
      <PostHogTracker>{children}</PostHogTracker>
    </PostHogProvider>
  );
}

/**
 * Internal component that handles tracking
 */
function PostHogTracker({ children }: PropsWithChildren) {
  const location = useLocation();
  const posthog = usePostHog();
  const { user } = useAuth();
  const lastPageview = useRef<string>();
  const lastUserId = useRef<string>();

  // Handle page views
  // biome-ignore lint/correctness/useExhaustiveDependencies: keeping the original code that is working
  useEffect(() => {
    if (!posthog) return;

    const currentUrl = window.location.href;
    if (lastPageview.current === currentUrl) return;

    lastPageview.current = currentUrl;
    posthog.capture("$pageview", {
      $current_url: currentUrl,
    });
  }, [location, posthog]);

  // Handle user identification and feature flag reloading
  useEffect(() => {
    if (!posthog || !user) return;

    if (lastUserId.current === user.id) return;

    lastUserId.current = user.id;

    // Normalize company name to title case for consistent feature flag matching
    const normalizeCompanyName = (name: string | undefined) => {
      if (!name) return name;

      // Special case handling for known company names
      const lowerName = name.toLowerCase();
      if (lowerName === "servicetitan") return "ServiceTitan";

      // General normalization: convert to title case
      return name
        .split(/[\s_-]+/)
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join("");
    };

    posthog.identify(user.id, {
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      company_id: user.company?.id,
      company_name: normalizeCompanyName(user.company?.name),
      organization_id: user.company?.default_organization?.id,
      organization_name: user.company?.default_organization?.name,
      role: user.company?.role,
    });

    // Reload feature flags after identification
    posthog.reloadFeatureFlags();
  }, [posthog, user]);

  return <>{children}</>;
}
